/**
 * Environment Viewer Devtools Panel
 *
 * Features:
 * - View current environment configuration
 * - Preview other environment configurations (read-only)
 * - JSON and GUI display modes
 * - Light/dark mode support
 * - No editing capabilities - pure viewer
 */

import { JsonEditor } from "json-edit-react";
import { useCallback, useEffect, useId, useState } from "react";
// Removed unused import
import { useConfiguration } from "../../contexts/ConfigProvider";

type ViewMode = "gui" | "json";
type Environment = "local" | "development" | "qa" | "production";

interface EnvironmentConfig {
	api?: {
		baseUrl?: string;
		timeout?: number;
		retryAttempts?: number;
		endpoints?: Record<string, string>;
		debug?: boolean;
	};
	flags?: Record<string, boolean>;
	ui?: {
		theme?: string;
		primaryColor?: string;
		brandLogo?: string;
		favicon?: string;
	};
	integrations?: {
		analytics?: {
			enabled?: boolean;
			trackingId?: string;
		};
		monitoring?: {
			enabled?: boolean;
			endpoint?: string;
			apiKey?: string;
		};
	};
	[key: string]: unknown; // Allow additional properties
}

export function EnvironmentViewer() {
	const currentConfig = useConfiguration();
	const [viewMode, setViewMode] = useState<ViewMode>("gui");
	const [selectedEnv, setSelectedEnv] = useState<Environment>("local");
	const envSelectorId = useId();
	const [envConfigs, setEnvConfigs] = useState<
		Record<Environment, EnvironmentConfig | null>
	>({
		local: null,
		development: null,
		qa: null,
		production: null,
	});
	const [isDarkMode, setIsDarkMode] = useState(false);

	// Detect dark mode from system or user preference
	useEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		setIsDarkMode(mediaQuery.matches);

		const handleChange = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);

	// Load all environment configurations
	useEffect(() => {
		const loadEnvironmentConfigs = async () => {
			try {
				const response = await fetch("/config.json");
				const fullConfig = await response.json();

				if (fullConfig.environments) {
					setEnvConfigs({
						local: fullConfig.environments.local || null,
						development: fullConfig.environments.development || null,
						qa: fullConfig.environments.qa || null,
						production: fullConfig.environments.production || null,
					});
				}
			} catch (error) {
				console.error("Failed to load environment configs:", error);
			}
		};

		loadEnvironmentConfigs();
	}, []);

	// Set selected environment to current when config loads
	useEffect(() => {
		if (currentConfig?.app?.environment) {
			setSelectedEnv(currentConfig.app.environment as Environment);
		}
	}, [currentConfig]);

	const handleEnvironmentChange = useCallback((env: Environment) => {
		setSelectedEnv(env);
	}, []);

	const getDisplayConfig = useCallback(() => {
		if (selectedEnv === currentConfig?.app?.environment) {
			// Show current resolved config for active environment
			return {
				api: currentConfig.api,
				flags: currentConfig.flags,
				ui: currentConfig.ui,
				integrations: currentConfig.integrations,
			};
		}
		// Show raw config for preview environment
		return envConfigs[selectedEnv];
	}, [selectedEnv, currentConfig, envConfigs]);

	const displayConfig = getDisplayConfig();
	const isCurrentEnv = selectedEnv === currentConfig?.app?.environment;

	if (!currentConfig) {
		return (
			<div
				className={`p-4 text-center ${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
			>
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2" />
				<p className="text-sm opacity-60">Loading configuration...</p>
			</div>
		);
	}

	const themeClasses = isDarkMode
		? "bg-gray-900 text-white border-gray-700"
		: "bg-white text-gray-900 border-gray-200";

	return (
		<div className={`h-full flex flex-col ${themeClasses}`}>
			{/* Header */}
			<div
				className={`border-b p-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
			>
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold">Environment Viewer</h2>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setIsDarkMode(!isDarkMode)}
							className={`p-2 rounded text-sm ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200"}`}
							title="Toggle theme"
						>
							{isDarkMode ? "☀️" : "🌙"}
						</button>
					</div>
				</div>

				{/* Environment Selector */}
				<div className="flex items-center gap-4 mb-4">
					<label htmlFor={envSelectorId} className="text-sm font-medium">
						Environment:
					</label>
					<select
						id={envSelectorId}
						value={selectedEnv}
						onChange={(e) =>
							handleEnvironmentChange(e.target.value as Environment)
						}
						className={`px-3 py-2 text-sm rounded border ${
							isDarkMode
								? "bg-gray-800 border-gray-600 text-white focus:ring-blue-400 focus:border-blue-400"
								: "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
						}`}
					>
						<option value="local">Local</option>
						<option value="development">Development</option>
						<option value="qa">QA</option>
						<option value="production">Production</option>
					</select>

					{isCurrentEnv && (
						<span className="px-2 py-1 text-xs bg-green-500 text-white rounded">
							ACTIVE
						</span>
					)}

					{!isCurrentEnv && (
						<span
							className={`px-2 py-1 text-xs rounded ${isDarkMode ? "bg-blue-800 text-blue-200" : "bg-blue-100 text-blue-800"}`}
						>
							PREVIEW
						</span>
					)}
				</div>

				{/* View Mode Toggle */}
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => setViewMode("gui")}
						className={`px-3 py-2 text-sm rounded ${
							viewMode === "gui"
								? "bg-blue-500 text-white"
								: isDarkMode
									? "bg-gray-700 text-gray-300 hover:bg-gray-600"
									: "bg-gray-200 text-gray-700 hover:bg-gray-300"
						}`}
					>
						GUI View
					</button>
					<button
						type="button"
						onClick={() => setViewMode("json")}
						className={`px-3 py-2 text-sm rounded ${
							viewMode === "json"
								? "bg-blue-500 text-white"
								: isDarkMode
									? "bg-gray-700 text-gray-300 hover:bg-gray-600"
									: "bg-gray-200 text-gray-700 hover:bg-gray-300"
						}`}
					>
						JSON View
					</button>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-auto">
				{!displayConfig ? (
					<div className="p-4 text-center">
						<p className="text-sm opacity-60">
							No configuration found for {selectedEnv} environment
						</p>
					</div>
				) : viewMode === "gui" ? (
					<GuiView config={displayConfig} isDarkMode={isDarkMode} />
				) : (
					<JsonView config={displayConfig} isDarkMode={isDarkMode} />
				)}
			</div>
		</div>
	);
}

// GUI View Component
function GuiView({
	config,
	isDarkMode,
}: {
	config: EnvironmentConfig;
	isDarkMode: boolean;
}) {
	const cardClasses = isDarkMode
		? "bg-gray-800 border-gray-700"
		: "bg-gray-50 border-gray-200";

	const valueClasses = isDarkMode
		? "bg-gray-900 border-gray-600 text-gray-100"
		: "bg-white border-gray-300 text-gray-900";

	return (
		<div className="p-4 space-y-6">
			{/* API Configuration */}
			<div className={`p-4 rounded-lg border ${cardClasses}`}>
				<h3 className="font-medium mb-3">API Configuration</h3>
				<div className="space-y-3">
					<ConfigField
						label="Base URL"
						value={config.api?.baseUrl || "Not configured"}
						valueClasses={valueClasses}
						mono
					/>
					<div className="grid grid-cols-2 gap-4">
						<ConfigField
							label="Timeout"
							value={`${config.api?.timeout || "Not configured"}ms`}
							valueClasses={valueClasses}
							mono
						/>
						<ConfigField
							label="Retry Attempts"
							value={String(config.api?.retryAttempts || "Not configured")}
							valueClasses={valueClasses}
							mono
						/>
					</div>
					<ConfigField
						label="Debug Mode"
						value={
							<span
								className={`px-2 py-1 rounded text-xs font-mono ${
									config.api?.debug
										? "bg-green-500 text-white"
										: "bg-red-500 text-white"
								}`}
							>
								{String(config.api?.debug || false)}
							</span>
						}
						valueClasses=""
					/>

					{/* API Endpoints */}
					{config.api?.endpoints && (
						<div className="mt-4">
							<h4 className="text-sm font-medium mb-2">Endpoints</h4>
							<div className="grid grid-cols-1 gap-2">
								{Object.entries(config.api.endpoints).map(([key, value]) => (
									<div key={key} className="flex justify-between text-sm">
										<span className="font-medium">{key}:</span>
										<span
											className={`font-mono ${isDarkMode ? "text-blue-300" : "text-blue-600"}`}
										>
											{value as string}
										</span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Feature Flags */}
			<div className={`p-4 rounded-lg border ${cardClasses}`}>
				<h3 className="font-medium mb-3">Feature Flags</h3>
				<div className="grid grid-cols-2 gap-3">
					{config.flags &&
						Object.entries(config.flags).map(([key, value]) => (
							<div key={key} className="flex items-center justify-between">
								<span className="text-sm font-medium">{key}</span>
								<span
									className={`px-2 py-1 rounded text-xs font-mono ${
										value ? "bg-green-500 text-white" : "bg-red-500 text-white"
									}`}
								>
									{String(value as boolean)}
								</span>
							</div>
						))}
				</div>
			</div>

			{/* UI Configuration */}
			<div className={`p-4 rounded-lg border ${cardClasses}`}>
				<h3 className="font-medium mb-3">UI Configuration</h3>
				<div className="space-y-3">
					<div className="grid grid-cols-2 gap-4">
						<ConfigField
							label="Theme"
							value={config.ui?.theme || "Not configured"}
							valueClasses={valueClasses}
							mono
						/>
						<ConfigField
							label="Primary Color"
							value={
								<div className="flex items-center gap-2">
									{config.ui?.primaryColor && (
										<div
											className="w-4 h-4 rounded border"
											style={{ backgroundColor: config.ui.primaryColor }}
										/>
									)}
									<span className="font-mono">
										{config.ui?.primaryColor || "Not configured"}
									</span>
								</div>
							}
							valueClasses={valueClasses}
						/>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<ConfigField
							label="Brand Logo"
							value={config.ui?.brandLogo || "Not configured"}
							valueClasses={valueClasses}
							mono
						/>
						<ConfigField
							label="Favicon"
							value={config.ui?.favicon || "Not configured"}
							valueClasses={valueClasses}
							mono
						/>
					</div>
				</div>
			</div>

			{/* Integrations */}
			<div className={`p-4 rounded-lg border ${cardClasses}`}>
				<h3 className="font-medium mb-3">Integrations</h3>
				<div className="space-y-4">
					{/* Analytics */}
					<IntegrationSection
						title="Analytics"
						enabled={config.integrations?.analytics?.enabled}
						fields={[
							{
								label: "Tracking ID",
								value:
									config.integrations?.analytics?.trackingId ||
									"Not configured",
							},
						]}
						valueClasses={valueClasses}
					/>

					{/* Monitoring */}
					<IntegrationSection
						title="Monitoring"
						enabled={config.integrations?.monitoring?.enabled}
						fields={[
							{
								label: config.integrations?.monitoring?.endpoint
									? "Endpoint"
									: "API Key",
								value:
									config.integrations?.monitoring?.endpoint ||
									config.integrations?.monitoring?.apiKey ||
									"Not configured",
							},
						]}
						valueClasses={valueClasses}
					/>
				</div>
			</div>
		</div>
	);
}

// Helper Components
function ConfigField({
	label,
	value,
	valueClasses,
	mono = false,
}: {
	label: string;
	value: React.ReactNode;
	valueClasses: string;
	mono?: boolean;
}) {
	return (
		<div>
			<div className="block text-sm font-medium mb-1">{label}</div>
			<div
				className={`px-3 py-2 text-sm rounded border ${mono ? "font-mono" : ""} ${valueClasses}`}
			>
				{value}
			</div>
		</div>
	);
}

function IntegrationSection({
	title,
	enabled,
	fields,
	valueClasses,
}: {
	title: string;
	enabled?: boolean;
	fields: Array<{ label: string; value: string }>;
	valueClasses: string;
}) {
	return (
		<div>
			<h4 className="text-sm font-medium mb-2">{title}</h4>
			<div className="grid grid-cols-2 gap-4">
				<div>
					<div className="block text-xs font-medium mb-1">Enabled</div>
					<span
						className={`px-2 py-1 rounded text-xs font-mono ${
							enabled ? "bg-green-500 text-white" : "bg-red-500 text-white"
						}`}
					>
						{String(enabled || false)}
					</span>
				</div>
				{fields.map((field) => (
					<div key={field.label}>
						<div className="block text-xs font-medium mb-1">{field.label}</div>
						<div
							className={`px-2 py-1 text-xs rounded border font-mono ${valueClasses}`}
						>
							{field.value}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// JSON View Component
function JsonView({
	config,
	isDarkMode,
}: {
	config: EnvironmentConfig;
	isDarkMode: boolean;
}) {
	return (
		<div className="h-full p-4">
			<div
				className={`h-full rounded border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
			>
				<JsonEditor
					data={config}
					setData={() => {}} // Read-only
					enableClipboard={true}
					showErrorMessages={false}
					showArrayIndices={true}
					collapse={1}
					rootName="config"
					restrictEdit={true}
					restrictDelete={true}
					restrictAdd={true}
				/>
			</div>
		</div>
	);
}
